import { useState } from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  ProfileEmployeeLocationEmployeesLocationFragment,
  ProfileEmployeeLocationFragment,
  ProfilePageDocument,
  UnassignLocationDocument,
  UpdateEmployeeDocument,
} from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import SetDefaultLocationsModal from 'modules/profile/components/Workplaces/SetDefaultLocationsModal'

interface Props {
  to: string
  employeeId: string
  accessId: string
  isLocationDefault: boolean
  locationId: string
  canAssignLocations: boolean
  onDefaultLocationUpdated: (id: string) => void
  companyLocations?: ProfileEmployeeLocationFragment[]
  employeeLocations: ProfileEmployeeLocationEmployeesLocationFragment[]
  employeesLocation?: ProfileEmployeeLocationEmployeesLocationFragment | null
}

const WorkplacesActions = ({
  to,
  employeeId,
  accessId,
  isLocationDefault,
  locationId,
  canAssignLocations,
  onDefaultLocationUpdated,
  companyLocations = [],
  employeeLocations,
  employeesLocation = null,
}: Props) => {
  const [showSetDefaultModal, setShowSetDefaultModal] = useState(false)
  const [unassignLocation] = useMutation(UnassignLocationDocument, {
    extractorFn: (data) => data.locationsMutations.unassignLocationsEmployeesLocation,
    refetchQueries: [ProfilePageDocument],
  })

  const [updateEmployee] = useMutation(UpdateEmployeeDocument, {
    extractorFn: (data) => data.employeesMutations.updateEmployeesEmployee,
  })

  if (!canAssignLocations) {
    return null
  }

  const removeFromLocation = async () => {
    const result = await unassignLocation({
      variables: {
        employeeId: employeeId,
        locationId: locationId,
      },
    })

    const handledResult = handleResult(result, (errors) => {
      if (errors) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('profile.employee.workplaces.remove_from_workplace_error'),
        })
      }
    })

    if (handledResult.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('profile.employee.workplaces.remove_from_workplace'),
      })
    }
  }

  const setAsDefaultLocation = async () => {
    if (employeesLocation && companyLocations.length > 0) {
      setShowSetDefaultModal(true)
      return
    }
    const result = await updateEmployee({
      variables: {
        id: employeeId,
        accessId: accessId,
        locationId: locationId,
      },
    })

    const handled = handleResult(result, (_errors) => {
      return
    })

    if (handled.success) onDefaultLocationUpdated(locationId)
  }

  const showActionsMenu = !isLocationDefault || (employeesLocation && companyLocations.length > 0)

  return (
    <Box flexDirection="row" gap="s12">
      {showActionsMenu && (
        <Box flexDirection="row" gap="s8" justifyContent="flexEnd">
          <PopoverMenu type="list">
            <PopoverMenu.Trigger>
              <Button.Decorative type="secondary" iconName="ellipsis" />
            </PopoverMenu.Trigger>
            <PopoverMenu.Content side="bottom" align="center">
              <PopoverMenu.TouchableOption
                title={i18n.t('employee_form.workplaces.set_default')}
                onClick={setAsDefaultLocation}
              />
              {!isLocationDefault && (
                <PopoverMenu.TouchableOption
                  title={i18n.t('employee_form.workplaces.remove_from_workplace')}
                  onClick={removeFromLocation}
                  danger
                />
              )}
            </PopoverMenu.Content>
          </PopoverMenu>
        </Box>
      )}
      <Button.Router to={to} type="secondary" iconName="arrow-right" />
      {showSetDefaultModal && employeesLocation && companyLocations.length > 0 && (
        <SetDefaultLocationsModal
          employeeLocations={employeeLocations}
          employeesLocation={employeesLocation}
          employeeId={employeeId}
          onClose={() => setShowSetDefaultModal(false)}
          onSuccess={(id) => {
            setShowSetDefaultModal(false)
            onDefaultLocationUpdated(id)
          }}
        />
      )}
    </Box>
  )
}

export default WorkplacesActions
